'use client';

import { userMessageForUploadFailure } from './authenticatedFetch';
import { validateFile, FILE_VALIDATION_CONFIGS } from './fileValidation';
import { fetchWithUploadRateLimitRetry } from './uploadRateLimitFetch';

interface UploadOptions {
  fileType?: keyof typeof FILE_VALIDATION_CONFIGS;
  maxRetries?: number;
  retryDelay?: number;
  skipValidation?: boolean; // 跳过文件验证（用于提交时）
}

interface UploadResult {
  success: boolean;
  url?: string;
  error?: string;
  retryCount?: number;
}

export class UploadError extends Error {
  constructor(
    message: string,
    public status?: number,
    public responseText?: string
  ) {
    super(message);
    this.name = 'UploadError';
  }
}

export async function uploadFile(
  file: File,
  endpoint: string,
  options: UploadOptions = {}
): Promise<UploadResult> {
  const {
    fileType = 'avatar',
    maxRetries = 3,
    retryDelay = 1000,
    skipValidation = false
  } = options;

  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // 验证文件（除非跳过验证）
      if (!skipValidation) {
        const validation = await validateFile(file, FILE_VALIDATION_CONFIGS[fileType]);
        if (!validation.isValid) {
          throw new UploadError(validation.error || '文件验证失败');
        }
      }

      if (typeof window !== 'undefined' && !localStorage.getItem('accessToken') && !localStorage.getItem('refreshToken')) {
        throw new UploadError(userMessageForUploadFailure(401));
      }

      const formData = new FormData();
      formData.append('file', file);

      const basePath = typeof window !== 'undefined' ? '/api' : `${process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3002'}/api`;
      const response = await fetchWithUploadRateLimitRetry(`${basePath}/upload/${endpoint}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errBody: Record<string, unknown> = {};
        try {
          errBody = JSON.parse(errorText) as Record<string, unknown>;
        } catch {
          errBody = { message: errorText };
        }
        throw new UploadError(
          userMessageForUploadFailure(response.status, errBody),
          response.status,
          errorText
        );
      }

      const result = await response.json();

      if (!result.file?.url) {
        throw new Error('文件上传成功但未返回URL');
      }

      return {
        success: true,
        url: result.file.url
      };

    } catch (error: any) {
      lastError = error;


      // 如果是最后一次尝试，抛出错误
      if (attempt === maxRetries) {
        return {
          success: false,
          error: error.message || '上传失败',
          retryCount: attempt
        };
      }

      // 等待后重试
      await new Promise(resolve => setTimeout(resolve, retryDelay));
    }
  }

  return {
    success: false,
    error: lastError?.message || '上传失败',
    retryCount: maxRetries
  };
}

export async function uploadAvatar(file: File): Promise<UploadResult> {
  return uploadFile(file, 'creator/avatar', { fileType: 'avatar' });
}

export async function uploadIdCard(
  file: File,
  cardType: 'front' | 'back' | 'hand'
): Promise<UploadResult> {
  const endpoint = `creator/idcard-${cardType}`;
  return uploadFile(file, endpoint, { fileType: 'idCard' });
}

export async function uploadCreatorGallery(file: File): Promise<UploadResult> {
  return uploadFile(file, 'creator/gallery', { fileType: 'gallery' });
}

export async function uploadAudio(file: File): Promise<UploadResult> {
  return uploadFile(file, 'audio', { fileType: 'audio' });
}

export async function uploadDocument(file: File): Promise<UploadResult> {
  return uploadFile(file, 'document', { fileType: 'document' });
} 

/**
 * 上传专辑证明文件（走 COS 的 albums/proofs 体系），支持自定义 path。
 * 用于“版权登记补交材料”把文件落到：albums/{albumId}/proofs/add_proofs/{caseId}/...
 */
export async function uploadAlbumProofWithPath(
  file: File,
  customPath: string,
  options: { maxRetries?: number; retryDelay?: number; skipValidation?: boolean } = {}
): Promise<UploadResult> {
  const { maxRetries = 3, retryDelay = 1000, skipValidation = false } = options;

  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      if (!skipValidation) {
        const validation = await validateFile(file, FILE_VALIDATION_CONFIGS.document);
        if (!validation.isValid) {
          throw new UploadError(validation.error || '文件验证失败');
        }
      }

      if (!localStorage.getItem('accessToken') && !localStorage.getItem('refreshToken')) {
        throw new UploadError(userMessageForUploadFailure(401));
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('path', customPath);

      const basePath = '/api';
      const response = await fetchWithUploadRateLimitRetry(`${basePath}/upload/album/proof`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errBody: Record<string, unknown> = {};
        try {
          errBody = JSON.parse(errorText) as Record<string, unknown>;
        } catch {
          errBody = { message: errorText };
        }
        throw new UploadError(
          userMessageForUploadFailure(response.status, errBody),
          response.status,
          errorText
        );
      }

      const result = await response.json();
      if (!result.file?.url) {
        throw new Error('文件上传成功但未返回URL');
      }

      return { success: true, url: result.file.url };
    } catch (error: any) {
      lastError = error;
      if (attempt === maxRetries) {
        return {
          success: false,
          error: error.message || '上传失败',
          retryCount: attempt
        };
      }
      await new Promise((resolve) => setTimeout(resolve, retryDelay * attempt));
    }
  }

  return { success: false, error: lastError?.message || '上传失败', retryCount: maxRetries };
}